import { queryOptions, useMutation } from "@tanstack/react-query";
import sortBy from "lodash-es/sortBy";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import { traderSchema } from "@/api/types/digitalnisklady.cz/types";
import { itemsPerPage } from "@/common/components/pagination/pagination";
import { entitySchema } from "@/common/schema/entity";
import { currencyFormatter } from "@/common/services/formatter";
import { graphql } from "@/generated/digitalnisklady.cz";
import { OrderInput } from "@/generated/digitalnisklady.cz/graphql";

// eslint-disable-next-line lingui/no-unlocalized-strings
const editOrderDocument = graphql(`
  mutation EditOrder($id: ID!, $order: OrderInput!) {
    editOrder(id: $id, order: $order) {
      id
    }
  }
`);

const useEditOrderMutation = () =>
  useMutation({
    mutationFn: ({ id, order }: { id: string; order: OrderInput }) =>
      graphqlClient.request(editOrderDocument, {
        id,
        order,
      }),
    mutationKey: ["edit-order"],
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const offerOrdersDocument = graphql(`
  query OrderList($id: ID!, $locale: String) {
    offer(id: $id) {
      id
      crop {
        id
        name(locale: $locale)
      }
      storage {
        id
        label
        company {
          id
          name
        }
      }
      orders {
        edges {
          node {
            id
            note
            amount
            currency
            quarter {
              id
              name
            }
            onlyTrader {
              id
              name
              logo
            }
            price
            lowPrice
            currency
            customTransport
            transportNote
          }
        }
      }
    }
  }
`);

const orderDetailSchema = z.object({
  company: entitySchema,
  crop: z.object({
    name: z.string(),
  }),
  order: z.object({
    amount: z.number(),
    currency: z.enum(["CZK", "EUR"]),
    customTransport: z.boolean(),
    customTransportNote: z
      .string()
      .nullish()
      .transform((v) => v ?? null),
    id: z.string(),
    note: z.string().optional(),
    totalPrice: z.string(),
    trader: traderSchema.nullish().transform((v) => v ?? null),
    unitLowPrice: z
      .number()
      .nullish()
      .transform((v) => v ?? null),
    unitPrice: z.number(),
  }),
  quarter: entitySchema,
  storage: entitySchema,
});

const orderDetailQuery = ({
  offerId,
  orderId,
  locale,
}: {
  offerId: string;
  orderId: string;
  locale: string;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(offerOrdersDocument, {
        id: offerId,
        locale,
      }),
    queryKey: ["offer", offerId, "orders", locale],
    select: (data) => {
      const result: Record<string, unknown> = {};
      const order = data.offer?.orders?.edges?.find(
        (edge) => edge?.node?.id === orderId,
      );
      result.company = data.offer?.storage?.company;
      result.storage = {
        id: data.offer?.storage?.id,
        name: data.offer?.storage?.label,
      };
      result.quarter = order?.node?.quarter;
      result.crop = data.offer?.crop;
      result.order = {
        amount: order?.node?.amount,
        currency: order?.node?.currency,
        customTransport: order?.node?.customTransport,
        customTransportNote: order?.node?.transportNote,

        id: order?.node?.id,
        note: order?.node?.note,

        // @ts-expect-error graphql types
        totalPrice: currencyFormatter(order?.node?.currency).format(
          // @ts-expect-error graphql types
          order.node?.amount * order?.node?.price,
        ),

        trader: order?.node?.onlyTrader,
        unitLowPrice: order?.node?.lowPrice,
        unitPrice: order?.node?.price,
      };

      return orderDetailSchema.parse(result);
    },
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const ordersDocument = graphql(`
  query Orders($companyId: ID!, $archived: Boolean, $locale: String) {
    company(id: $companyId) {
      storages(archived: $archived) {
        edges {
          node {
            label
            offers {
              edges {
                node {
                  id
                  crop {
                    id
                    name(locale: $locale)
                  }
                  orders {
                    edges {
                      node {
                        id
                        active
                        amount
                        currency
                        price
                        quarter {
                          id
                          name
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

const ordersQuery = ({
  companyId,
  locale,
}: {
  companyId: string;
  locale: string;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(ordersDocument, {
        archived: false,
        companyId,
        locale,
      }),
    queryKey: ["orders", companyId, locale],
  });

const cropsInOrdersQuery = ({
  companyId,
  locale,
}: {
  companyId: string;
  locale: string;
}) =>
  queryOptions({
    ...ordersQuery({ companyId, locale }),
    select: (data) => {
      const crops: { id: string; name: string }[] = [];
      data.company?.storages?.edges?.forEach((storage) =>
        storage?.node?.offers?.edges?.forEach((offer) => {
          if (crops.find((item) => item.id === offer?.node?.crop?.id)) {
            return;
          }
          // @ts-expect-error graphql types are crazy
          crops.push(offer?.node?.crop);
        }),
      );

      const sortedCrops = sortBy(crops, (c) => c.name);
      return z.array(entitySchema).parse(sortedCrops);
    },
  });

const orderSchema = z.object({
  crop: entitySchema,
  offer: z.object({
    id: z.string(),
  }),
  order: z.object({
    amount: z.number(),
    currency: z.enum(["CZK", "EUR"]),
    id: z.string(),
    price: z.number(),
    quarter: entitySchema,
  }),
  storage: z.object({
    name: z.string(),
  }),
});

const orderListQuery = ({
  companyId,
  cropIds,
  page,
  locale,
}: {
  companyId: string;
  cropIds: string[];
  page: number;
  locale: string;
}) =>
  queryOptions({
    ...ordersQuery({ companyId, locale }),
    queryKey: [
      ...ordersQuery({ companyId, locale }).queryKey,
      cropIds.toSorted().join(","),
      page.toString(),
    ],
    select: (data) => {
      const arraySchema = z.array(orderSchema);
      const orders: z.infer<typeof arraySchema> = [];
      data.company?.storages?.edges?.forEach((storage) =>
        storage?.node?.offers?.edges
          ?.filter((offer) =>
            cropIds.length === 0
              ? true
              : // @ts-expect-error insane graphql types
                cropIds.includes(offer?.node?.crop?.id),
          )
          .forEach((offer) => {
            offer?.node?.orders?.edges
              ?.filter((order) => order?.node?.active)
              .forEach((order) => {
                orders.push({
                  crop: {
                    // @ts-expect-error insane graphql types
                    id: offer.node?.crop?.id,
                    // @ts-expect-error insane graphql types
                    name: offer.node?.crop?.name,
                  },
                  offer: {
                    // @ts-expect-error insane graphql types
                    id: offer.node?.id,
                  },
                  order: {
                    // @ts-expect-error insane graphql types
                    amount: order?.node?.amount,

                    // @ts-expect-error insane graphql types
                    currency: order?.node?.currency,

                    // @ts-expect-error insane graphql types
                    id: order?.node?.id,

                    // @ts-expect-error insane graphql types
                    price: order?.node?.price,

                    // @ts-expect-error insane graphql types
                    quarter: order?.node?.quarter,
                  },
                  storage: {
                    // @ts-expect-error insane graphql types
                    name: storage.node?.label,
                  },
                });
              });
          }),
      );

      const start = itemsPerPage.ten * (page - 1);
      const end = start + itemsPerPage.ten;

      return {
        orders: arraySchema.parse(orders).slice(start, end),
        total: orders.length,
      };
    },
  });

export {
  cropsInOrdersQuery,
  orderDetailQuery,
  orderListQuery,
  ordersQuery,
  useEditOrderMutation,
};
